import { format, parseISO } from "date-fns"
import { Link, useLocation } from "react-router-dom"
import { LettermemeViewFragment, NewsletterViewFragment } from "~/__generated__/graphql"
import { lettermemeLatestPath, lettermemePath, lettermemeWeekInReviewPath } from "~/common/paths"
import Text from "~/ui/typography"
import { SubscribeForm } from "./subscribe-form"

const NEWSLETTER_OVERVIEW_MAX_CHARS = 220

interface NewsletterViewProps {
  lettermeme: LettermemeViewFragment
  newsletter: NewsletterViewFragment
  variant?: "full" | "preview"
}

export const NewsletterView: React.FC<NewsletterViewProps> = ({
  lettermeme,
  newsletter,
  variant = "full",
}) => {
  const location = useLocation()
  const isPreview = variant === "preview"
  const summaryOverview = (newsletter.shortOverview || newsletter.overview || "").trim()
  const shouldTrimSummaryOverview =
    summaryOverview.length > 0 && summaryOverview.length > NEWSLETTER_OVERVIEW_MAX_CHARS
  const summaryOverviewText = shouldTrimSummaryOverview
    ? `${summaryOverview.slice(0, NEWSLETTER_OVERVIEW_MAX_CHARS).trimEnd()}…`
    : summaryOverview
  const summaryTitle = newsletter.title || "Newsletter"
  const showSummaryInFull = !lettermeme.monetizationEnabled
  const endDate = parseISO(newsletter.createdAt)
  const dateLabel = format(endDate, "MM-dd-yyyy")
  const nowPath = lettermemeLatestPath({ urlPath: lettermeme.urlPath })
  const weekInReviewPath = lettermemeWeekInReviewPath({ urlPath: lettermeme.urlPath })
  const archivePath = lettermemePath({ urlPath: lettermeme.urlPath })
  const isNowActive = location.pathname === nowPath
  const isWeekInReviewActive = location.pathname === weekInReviewPath
  const isArchiveActive =
    location.pathname === archivePath ||
    location.pathname.startsWith(`${lettermemePath({ urlPath: lettermeme.urlPath })}/archives/`)
  const navLinkClass = (active: boolean) =>
    active ? "font-semibold text-gray-200 underline" : "text-gray-400 underline"

  return (
    <div className="">
      <div className="mb-6">
        <Text as="h1" className="text-xl font-bold">
          {lettermeme.name}
        </Text>
        {newsletter && (
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-2 text-xs italic text-gray-400">
              <span className="text-gray-400">Updated {dateLabel}</span>
              <span aria-hidden="true">•</span>
              <Link to={nowPath} className={navLinkClass(isNowActive)}>
                Now
              </Link>
              <span aria-hidden="true">•</span>
              <Link to={weekInReviewPath} className={navLinkClass(isWeekInReviewActive)}>
                Week in Review
              </Link>
              <span aria-hidden="true">•</span>
              <Link to={archivePath} className={navLinkClass(isArchiveActive)}>
                Full Archive
              </Link>
            </div>
          </div>
        )}
      </div>
      {lettermeme.publicDescription && (
        <Text as="div" className="mb-6 text-gray-400">
          {lettermeme.publicDescription}
        </Text>
      )}
      <div className="mb-8">
        <SubscribeForm
          lettermemeId={lettermeme.id}
          isSubscribed={lettermeme.viewerIsSubscribed ?? false}
          isPrivate={lettermeme.privacy === "PRIVATE"}
          monetizationEnabled={lettermeme.monetizationEnabled}
          subscriptionPriceCents={lettermeme.subscriptionPriceCents}
          subscriptionCurrency={lettermeme.subscriptionCurrency}
        />
      </div>
      <div>
        {newsletter.originalImageUrl && (
          <img src={newsletter.originalImageUrl} className="mb-10" height={1536} width={1024} />
        )}

        {isPreview ? (
          <div className="space-y-3">
            <Text as="h3" className="text-xl font-bold">
              {summaryTitle}
            </Text>
            {summaryOverviewText && (
              <Text as="p" className="text-sm text-gray-300">
                {summaryOverviewText}
              </Text>
            )}
            <div className="rounded-lg border border-gray-800 bg-gray-900/40 p-4">
              <Text className="text-sm text-gray-200">
                Sign up or login to see the full lettermeme.
              </Text>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {showSummaryInFull && (
              <div className="space-y-2">
                <Text as="h3" className="text-xl font-bold">
                  {summaryTitle}
                </Text>
                {summaryOverviewText && (
                  <Text as="p" className="text-sm text-gray-300">
                    {summaryOverviewText}
                  </Text>
                )}
              </div>
            )}
            {newsletter.topics.length > 0 && (
              <div className="space-y-6">
                {newsletter.topics.map((topic, index) => (
                  <div key={index} className="border-l-2 border-gray-300 pl-5">
                    <Text as="h4" className="mb-3">
                      {topic.topic}
                    </Text>
                    {topic.opinions.length > 0 && (
                      <div className="space-y-3">
                        {topic.opinions.map((opinion, opinionIndex) => (
                          <div key={opinionIndex} className="text-sm text-gray-300">
                            {opinion.opinion}
                            <div className="text-xs italic text-gray-400">
                              {opinion.sourceUrl ? (
                                <a href={opinion.sourceUrl} target="_blank" className="underline">
                                  — {opinion.newsletterName}
                                </a>
                              ) : (
                                <>— {opinion.newsletterName}</>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
